import React, { useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';

import {
  changeSecretCode,
  InvalidSecretCodeError,
  NotEnrolledError,
} from '../../services/encryption/MasterKeyService';
import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';

import backIcon from '../../assets/icons/back.png';
import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';

const logger = createLogger('ChangeRecoveryCode');

interface Props {
  navigation: NavigationProp<any>;
}

const MIN_LEN = 8;

/**
 * Rotate the recovery code. Requires proof of the existing code (old
 * verifier must match backend's stored verifier) before the new wrapped
 * master key + verifier are accepted.
 *
 * MasterKeyService.changeSecretCode handles:
 *  - Fetching the current wrapped key from the backend.
 *  - Unwrapping locally to prove knowledge of the old code.
 *  - Re-wrapping with a KEK derived from the new code.
 *  - POSTing the new envelope + both verifiers.
 *
 * Throws InvalidSecretCodeError on wrong old code (never touches the
 * server) and NotEnrolledError if the user hasn't actually enrolled
 * yet (shouldn't reach this screen in that case, but handled defensively).
 */
const ChangeRecoveryCodeScreen: React.FC<Props> = ({ navigation }) => {
  const [oldCode, setOldCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit =
    oldCode.length >= MIN_LEN &&
    newCode.length >= MIN_LEN &&
    confirm.length >= MIN_LEN &&
    !submitting;

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (newCode.length < MIN_LEN) {
      setErrorMessage(`New recovery code must be at least ${MIN_LEN} characters`);
      return;
    }
    if (newCode !== confirm) {
      setErrorMessage('The two entries of the new code do not match.');
      return;
    }
    if (newCode === oldCode) {
      setErrorMessage('New recovery code must differ from the old one.');
      return;
    }
    setSubmitting(true);
    try {
      await changeSecretCode(oldCode, newCode);
      Alert.alert(
        'Recovery code updated',
        'Your new recovery code is saved. Write it down somewhere safe.',
        [{ text: 'OK', onPress: () => navigation.goBack() }],
      );
    } catch (err) {
      if (err instanceof InvalidSecretCodeError) {
        setErrorMessage('That does not match your current recovery code.');
      } else if (err instanceof NotEnrolledError) {
        setErrorMessage(
          'You have not set a recovery code yet. Close this screen and choose "Set up recovery code".',
        );
      } else {
        logger.error('Recovery-code change failed');
        setErrorMessage(
          err instanceof Error ? err.message : 'Something went wrong.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Change Recovery Code</Text>
        <View style={{ width: ms(24) }} />
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.description}>
            Enter your current recovery code, then the new one. Your vault data
            is re-wrapped on your device — we never see either code.
          </Text>

          <Text style={styles.label}>Current recovery code</Text>
          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="Current code"
              placeholderTextColor="#9A9A9A"
              value={oldCode}
              onChangeText={setOldCode}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              textContentType="password"
            />
            <TouchableOpacity
              style={styles.eyeToggle}
              onPress={() => setShowCode((v) => !v)}
            >
              <Image
                source={showCode ? eyeIcon : eyeClosedIcon}
                style={styles.eyeIcon}
              />
            </TouchableOpacity>
          </View>

          <Text style={styles.label}>New recovery code</Text>
          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="New code (min 8 chars)"
              placeholderTextColor="#9A9A9A"
              value={newCode}
              onChangeText={setNewCode}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="newPassword"
            />
          </View>

          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder="Confirm new code"
              placeholderTextColor="#9A9A9A"
              value={confirm}
              onChangeText={setConfirm}
              secureTextEntry={!showCode}
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="newPassword"
            />
          </View>

          {errorMessage ? (
            <Text style={styles.error} accessibilityRole="alert">
              {errorMessage}
            </Text>
          ) : null}

          <TouchableOpacity
            style={[
              styles.primaryButton,
              !canSubmit && styles.primaryButtonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={!canSubmit}
            accessibilityRole="button"
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>Update recovery code</Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: {
    padding: s(4),
  },
  backIcon: {
    width: ms(24),
    height: ms(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(16),
    fontWeight: '600',
    color: '#1A1A1A',
  },
  content: {
    padding: s(20),
  },
  description: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
    marginBottom: vs(20),
  },
  label: {
    fontSize: ms(13),
    fontWeight: '600',
    color: '#1A1A1A',
    marginTop: vs(12),
    marginBottom: vs(6),
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D8DBDD',
    borderRadius: ms(8),
    marginBottom: vs(8),
    paddingHorizontal: s(12),
  },
  input: {
    flex: 1,
    fontSize: ms(15),
    color: '#1A1A1A',
    paddingVertical: vs(14),
  },
  eyeToggle: {
    padding: s(8),
  },
  eyeIcon: {
    width: ms(20),
    height: ms(20),
    tintColor: '#666',
  },
  error: {
    fontSize: ms(13),
    color: '#E85C5C',
    marginTop: vs(8),
    marginBottom: vs(4),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    paddingHorizontal: s(40),
    borderRadius: ms(8),
    alignItems: 'center',
    marginTop: vs(16),
  },
  primaryButtonDisabled: {
    backgroundColor: '#B5E3E3',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
});

export default ChangeRecoveryCodeScreen;
